import { useCallback, useEffect, useRef, useState } from "react";

const clock = () => {
  const now = new Date();
  return [now.getHours(), now.getMinutes(), now.getSeconds()]
    .map((part) => String(part).padStart(2, "0"))
    .join(":");
};

/**
 * Drives the pipeline console from the backend's own progress stream.
 *
 * Every stage shown is a real event emitted by the analysis code as it runs —
 * `render`, `project`, `extract`, `fingerprint`, `score`, `report`, `classify` —
 * so nothing here is simulated. Stages the server never reports stay pending.
 *
 * Stage ids repeat: a two-document comparison walks render/project/extract once
 * per document, and once per page within it. So progress is tracked as "seen"
 * plus "current" rather than as a monotonic index, which keeps a second pass
 * from appearing to move the run backwards.
 */
export function usePipeline(stages) {
  const [status, setStatus] = useState("idle"); // idle | uploading | working | done | failed
  const [current, setCurrent] = useState(null);
  const [seen, setSeen] = useState(() => new Set());
  const [details, setDetails] = useState({});
  const [failedStage, setFailedStage] = useState(null);
  const [log, setLog] = useState([]);
  const [elapsed, setElapsed] = useState(0);
  const startedAt = useRef(0);
  const lineId = useRef(0);

  const running = status === "uploading" || status === "working";

  useEffect(() => {
    if (!running) return undefined;
    const timer = window.setInterval(() => {
      setElapsed((Date.now() - startedAt.current) / 1000);
    }, 100);
    return () => window.clearInterval(timer);
  }, [running]);

  const append = useCallback((message, tone = "info") => {
    lineId.current += 1;
    setLog((prev) => [...prev.slice(-60), { id: lineId.current, ts: clock(), msg: message, tone }]);
  }, []);

  const start = useCallback(
    (message) => {
      startedAt.current = Date.now();
      setElapsed(0);
      setCurrent("upload");
      setSeen(new Set(["upload"]));
      setDetails({});
      setFailedStage(null);
      setStatus("uploading");
      lineId.current += 1;
      setLog([{ id: lineId.current, ts: clock(), msg: message, tone: "info" }]);
    },
    []
  );

  /** Feed one parsed NDJSON event from the backend. */
  const handleEvent = useCallback(
    (event) => {
      if (event.event === "stage") {
        // The first server-side stage proves the upload finished.
        setStatus("working");
        setCurrent(event.stage);
        setSeen((prev) => {
          const next = new Set(prev);
          next.add("upload");
          next.add(event.stage);
          return next;
        });
        if (event.detail) {
          setDetails((prev) => ({ ...prev, [event.stage]: event.detail }));
        }
        return;
      }
      if (event.event === "log") {
        append(event.message, event.tone || "info");
      }
    },
    [append]
  );

  const succeed = useCallback(
    (message) => {
      setStatus("done");
      setCurrent(null);
      append(message, "ok");
    },
    [append]
  );

  const fail = useCallback(
    (message) => {
      setStatus("failed");
      setFailedStage((prevFailed) => prevFailed ?? current);
      setCurrent(null);
      append(message, "err");
    },
    [append, current]
  );

  const reset = useCallback(() => {
    setStatus("idle");
    setCurrent(null);
    setSeen(new Set());
    setDetails({});
    setFailedStage(null);
    setLog([]);
    setElapsed(0);
  }, []);

  const stageStates = stages.map((stage) => {
    if (status === "idle") return "pending";
    if (status === "done") return "done";
    if (status === "failed") {
      if (stage.id === failedStage) return "failed";
      return seen.has(stage.id) ? "done" : "pending";
    }
    if (stage.id === current) return "active";
    return seen.has(stage.id) ? "done" : "pending";
  });

  return {
    status,
    running,
    stageStates,
    stageDetails: details,
    log,
    elapsed,
    append,
    start,
    handleEvent,
    succeed,
    fail,
    reset,
  };
}

export default usePipeline;
